import { type ReactNode, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/ui/button';
import { Card, CardContent } from 'components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { Skeleton } from 'components/ui/skeleton';
import { useTheme } from 'components/core/theme-provider';
import { cn } from 'lib/utils';
import { primaryButtonClass } from '../constants/button-styles';
import { useGetBillingProfile } from '../hooks/use-subscription';
import { BillingDetailsForm, BillingDetailsFormHandle } from './billing-details-form';

/** "CH" becomes "Switzerland"; a code the browser does not know is shown as it is. */
const countryName = (code: string) => {
  try {
    return new Intl.DisplayNames(undefined, { type: 'region' }).of(code.toUpperCase()) ?? code;
  } catch {
    return code;
  }
};

interface BillingDetailsCardProps {
  /** A line under the title, as on the Account page. */
  description?: string;
  /** Shown under the details, above a divider. */
  footer?: ReactNode;
}

/** What goes on every invoice, with an Edit dialog that saves it. */
export const BillingDetailsCard = ({ description, footer }: BillingDetailsCardProps) => {
  const { t } = useTranslation();
  const { data: profile, isLoading } = useGetBillingProfile();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const billingRef = useRef<BillingDetailsFormHandle>(null);
  const isDark = useTheme().resolvedTheme === 'dark';

  const handleSave = async () => {
    setIsSaving(true);
    const isSaved = await billingRef.current?.save();
    setIsSaving(false);
    if (isSaved) setIsEditing(false);
  };

  if (isLoading) return <Skeleton className="h-44 rounded-section lg:col-span-3" />;

  const address = profile?.address;
  const rows = [
    { label: t('BILLING_COMPANY', 'Company'), value: profile?.legalName },
    {
      label: t('ADDRESS', 'Address'),
      value:
        address &&
        [
          address.line1,
          address.line2,
          [address.postalCode, address.city].filter(Boolean).join(' '),
          address.countryCode && countryName(address.countryCode),
        ]
          .filter(Boolean)
          .join(', '),
    },
    { label: t('VAT_NUMBER', 'VAT number'), value: profile?.taxRegistrationId },
    { label: t('INVOICES_GO_TO', 'Invoices go to'), value: profile?.billingContactEmail },
  ];

  return (
    <Card className="min-w-0 rounded-section border-border/70 shadow-none lg:col-span-3">
      <CardContent className="flex h-full flex-col gap-4 p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 flex-col gap-0.5">
            <h2 className={cn('font-medium text-foreground', description ? 'text-base leading-heading' : 'text-sm')}>
              {t('BILLING_DETAILS', 'Billing details')}
            </h2>
            {description && <p className="text-sm text-muted-foreground">{description}</p>}
          </div>
          <Button variant="outline" size="sm" className="shrink-0 rounded-full" onClick={() => setIsEditing(true)}>
            {t('EDIT', 'Edit')}
          </Button>
        </div>

        <dl className="flex flex-col gap-2 text-sm">
          {rows.map((row) => (
            <div key={row.label} className="flex flex-col gap-0.5 sm:flex-row sm:gap-4">
              <dt className="shrink-0 text-muted-foreground sm:w-28">{row.label}</dt>
              <dd className="min-w-0 break-words text-foreground">{row.value || '—'}</dd>
            </div>
          ))}
        </dl>

        {footer && <div className="border-t border-border pt-3">{footer}</div>}
      </CardContent>

      <Dialog open={isEditing} onOpenChange={(isOpen) => !isSaving && setIsEditing(isOpen)}>
        {/* Header and footer stay put; only the form scrolls on short screens. */}
        <DialogContent className="!flex max-h-[90dvh] w-[calc(100vw-2rem)] flex-col gap-0 overflow-hidden p-0 sm:max-w-xl">
          <DialogHeader className="px-6 pb-4 pt-6">
            <DialogTitle>{t('EDIT_BILLING_DETAILS', 'Edit billing details')}</DialogTitle>
            <DialogDescription>
              {t('BILLING_DETAILS_FUTURE_INVOICES', 'These appear on your future invoices.')}
            </DialogDescription>
          </DialogHeader>
          <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-4">
            <BillingDetailsForm ref={billingRef} purpose="settings" hideActions />
          </div>
          <DialogFooter className="gap-2 px-6 pb-6 pt-2">
            <Button
              variant="ghost"
              className="w-full rounded-full px-6 sm:w-auto"
              disabled={isSaving}
              onClick={() => setIsEditing(false)}
            >
              {t('CANCEL', 'Cancel')}
            </Button>
            <Button
              className={cn('w-full rounded-full px-6 sm:w-auto', primaryButtonClass(isDark))}
              loading={isSaving}
              disabled={isSaving}
              onClick={handleSave}
            >
              {t('SAVE_DETAILS', 'Save details')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
